import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { roleHome } from "./lib/format";
import Layout from "./components/Layout";

import Login from "./pages/Login";
import AdminDashboard from "./pages/dashboards/AdminDashboard";
import ManagerDashboard from "./pages/dashboards/ManagerDashboard";
import LegalDashboard from "./pages/dashboards/LegalDashboard";
import TenantDashboard from "./pages/dashboards/TenantDashboard";
import MaintenanceDashboard from "./pages/dashboards/MaintenanceDashboard";
import Units from "./pages/Units";
import Tenants from "./pages/Tenants";
import Pricing from "./pages/Pricing";
import Leases from "./pages/Leases";
import LeaseDetail from "./pages/LeaseDetail";
import Payments from "./pages/Payments";
import Maintenance from "./pages/Maintenance";
import Feedback from "./pages/Feedback";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import AuditLogs from "./pages/AuditLogs";
import Onboarding from "./pages/Onboarding";
import ForgotPassword from "./pages/ForgotPassword";
import TenantPayments from "./pages/TenantPayments";
import TenantLease from "./pages/TenantLease";
import Inspections from "./pages/Inspections";
import Refunds from "./pages/Refunds";
import Expenses from "./pages/Expenses";
import UtilityBills from "./pages/UtilityBills";
import Communications from "./pages/Communications";
import Arrears from "./pages/Arrears";
import PublicHome from "./pages/PublicHome";
import ResetPassword from "./pages/ResetPassword";
import Leads from "./pages/Leads";
import Staff from "./pages/Staff";
import Profile from "./pages/Profile";
import SecurityAlerts from "./pages/SecurityAlerts";
import PromoBanner from "./pages/PromoBanner";
import LeaseRecycleBin from "./pages/LeaseRecycleBin";

function ProtectedRoute({ children, allowedRoles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="h-screen flex items-center justify-center text-[#5E7166]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-[#5E7166]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={roleHome(user.role)} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/paystack/checkout/:ref" element={<Navigate to="/payments" replace />} />

          <Route path="/" element={<PublicHome />} />
          <Route path="/dashboard" element={<HomeRedirect />} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/manager" element={<ProtectedRoute allowedRoles={["admin","manager"]}><ManagerDashboard /></ProtectedRoute>} />
          <Route path="/legal" element={<ProtectedRoute allowedRoles={["admin","lawyer"]}><LegalDashboard /></ProtectedRoute>} />
          <Route path="/maintenance-staff" element={<ProtectedRoute allowedRoles={["admin","maintenance"]}><MaintenanceDashboard /></ProtectedRoute>} />

          {/* Shared */}
          <Route path="/units" element={<ProtectedRoute allowedRoles={["admin","manager","lawyer"]}><Units /></ProtectedRoute>} />
          <Route path="/tenants" element={<ProtectedRoute allowedRoles={["admin","manager","lawyer"]}><Tenants /></ProtectedRoute>} />
          <Route path="/pricing" element={<ProtectedRoute allowedRoles={["admin","manager","lawyer"]}><Pricing /></ProtectedRoute>} />
          <Route path="/leases" element={<ProtectedRoute allowedRoles={["admin","manager","lawyer"]}><Leases /></ProtectedRoute>} />
          <Route path="/leases/:leaseId" element={<ProtectedRoute allowedRoles={["admin","manager","lawyer"]}><LeaseDetail /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Payments /></ProtectedRoute>} />
          <Route path="/maintenance" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Maintenance /></ProtectedRoute>} />
          <Route path="/feedback" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Feedback /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute allowedRoles={["admin","manager","lawyer"]}><Reports /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute allowedRoles={["admin"]}><Settings /></ProtectedRoute>} />
          <Route path="/promo-banner" element={<ProtectedRoute allowedRoles={["admin","manager"]}><PromoBanner /></ProtectedRoute>} />
          <Route path="/lease-recycle-bin" element={<ProtectedRoute allowedRoles={["admin"]}><LeaseRecycleBin /></ProtectedRoute>} />
          <Route path="/audit-logs" element={<ProtectedRoute allowedRoles={["admin"]}><AuditLogs /></ProtectedRoute>} />
          <Route path="/inspections" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Inspections /></ProtectedRoute>} />
          <Route path="/refunds" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Refunds /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Expenses /></ProtectedRoute>} />
          <Route path="/utility-bills" element={<ProtectedRoute allowedRoles={["admin","manager"]}><UtilityBills /></ProtectedRoute>} />
          <Route path="/communications" element={<ProtectedRoute allowedRoles={["admin","manager","lawyer"]}><Communications /></ProtectedRoute>} />
          <Route path="/arrears" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Arrears /></ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute allowedRoles={["admin","manager"]}><Leads /></ProtectedRoute>} />
          <Route path="/staff" element={<ProtectedRoute allowedRoles={["admin"]}><Staff /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/security-alerts" element={<ProtectedRoute allowedRoles={["admin","manager","tenant"]}><SecurityAlerts /></ProtectedRoute>} />

          {/* Tenant */}
          <Route path="/tenant" element={<ProtectedRoute allowedRoles={["tenant"]}><TenantDashboard /></ProtectedRoute>} />
          <Route path="/tenant/lease" element={<ProtectedRoute allowedRoles={["tenant"]}><TenantLease /></ProtectedRoute>} />
          <Route path="/tenant/payments" element={<ProtectedRoute allowedRoles={["tenant"]}><TenantPayments /></ProtectedRoute>} />
          <Route path="/tenant/maintenance" element={<ProtectedRoute allowedRoles={["tenant"]}><Maintenance /></ProtectedRoute>} />
          <Route path="/tenant/onboarding" element={<ProtectedRoute allowedRoles={["tenant"]}><Onboarding /></ProtectedRoute>} />
          <Route path="/tenant/feedback" element={<ProtectedRoute allowedRoles={["tenant"]}><Feedback /></ProtectedRoute>} />

          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
